/**
 * @component ry-tooltip
 * @category  overlay
 * @summary   Hover- and focus-triggered tooltip (Fomantic UI Popup equivalent). Wraps a single trigger child, sets `aria-describedby` to link the tooltip text to the trigger, and reveals after a short delay on `mouseenter` / `focusin`. Closes on `mouseleave` / `focusout` / Escape.
 *
 * Reference: Fomantic UI's popup module. ry-tooltip is the text-only variant —
 * for richer popovers (with arbitrary HTML, action buttons, etc.) compose
 * `<ry-dropdown>` instead.
 *
 * @attribute content    string?              The tooltip text. Required for visible output.
 * @attribute position   'top' | 'bottom' | 'left' | 'right' ?  default="top"   Side of the trigger to anchor the tooltip to.
 * @attribute delay      number?   default=200   Milliseconds to wait after mouseenter/focusin before showing. Set to 0 for instant show. Applies to hover and focus equally.
 *
 * @property  state      'open' | 'closed'    Mirrors `data-ry-state`.
 *
 * @method    show()     Immediately show the tooltip (bypasses any pending delay). Sets `data-ry-position` from the attribute. Fires `ry:show`. No-op if already open.
 * @method    hide()     Immediately hide the tooltip and cancel any pending show. Fires `ry:hide`. No-op if already closed.
 *
 * @event ry:beforeshow
 *        detail: {}
 *        fires:  in `show()` — hover/focus delay elapsed, or `el.show()` — after the already-open guard, before `data-ry-position` or state change
 *        cancelable: preventDefault() keeps the tooltip closed; `ry:show` does not fire
 *
 * @event ry:show
 *        detail: {}
 *        fires:  after the tooltip transitions to open
 *
 * @event ry:beforehide
 *        detail: {}
 *        fires:  in `hide()` — mouseleave, focusout, Escape, or `el.hide()` — after the already-closed guard, before any state change
 *        cancelable: preventDefault() keeps the tooltip open; `ry:hide` does not fire
 *
 * @event ry:hide
 *        detail: {}
 *        fires:  after the tooltip transitions to closed
 *
 * @keyboard  Escape — hides the tooltip when open. The trigger child handles its own keyboard naturally (this component only listens on the document for Escape).
 *
 * @state     `data-ry-state` of `"open"` / `"closed"` on host. `data-ry-position` set to the resolved position on show; cleared (well, retained) so CSS can read it.
 *
 * @content   Exactly one trigger child. The tooltip element is appended after that trigger automatically and references it via `aria-describedby`.
 *
 * @dom
 *   ry-tooltip[data-ry-state="open|closed"][data-ry-position="top|bottom|left|right"]
 *     > {trigger child}    aria-describedby="ry-tooltip-N"
 *     > div.ry-tooltip__content    [data-ry-target="content"]   id="ry-tooltip-N" role="tooltip"
 *         > {content text}
 *
 * @gotcha  The trigger must be a focusable element (button, link, input). On non-focusable triggers, keyboard users never see the tooltip — they cannot focus into it. happy-dom cannot enforce this; rely on `<ry-button>` or native focusables.
 *
 * @gotcha  `content` is plain text only. HTML inside the attribute is escaped — there is no rich content path. For richer popovers use `<ry-dropdown>`.
 *
 * @gotcha  The 200ms default delay means tests need to wait — call `el.show()` directly to bypass the delay in deterministic tests, or set `delay="0"` on the element.
 *
 * @gotcha  Only ONE tooltip listens to document keydown at a time per instance, but each instance adds its own listener. Many tooltips on a page is fine; the listener cost is O(N) but the work is constant per instance (just an Escape check).
 *
 * @example basic
 * ```html
 * <ry-tooltip content="Save your changes">
 *   <ry-button>Save</ry-button>
 * </ry-tooltip>
 * ```
 *
 * @example position-bottom
 * ```html
 * <ry-tooltip content="Delete item" position="bottom">
 *   <ry-button variant="danger">Delete</ry-button>
 * </ry-tooltip>
 * ```
 *
 * @example positions
 * ```html
 * <ry-tooltip content="Top"    position="top"   ><ry-button>Top</ry-button></ry-tooltip>
 * <ry-tooltip content="Right"  position="right" ><ry-button>Right</ry-button></ry-tooltip>
 * <ry-tooltip content="Bottom" position="bottom"><ry-button>Bottom</ry-button></ry-tooltip>
 * <ry-tooltip content="Left"   position="left"  ><ry-button>Left</ry-button></ry-tooltip>
 * ```
 *
 * @example instant-no-delay
 * ```html
 * <ry-tooltip content="Instant" delay="0">
 *   <ry-button>Hover me</ry-button>
 * </ry-tooltip>
 * ```
 *
 * @tutorial show-and-hide-programmatic
 * ```js
 * const el = mount('<ry-tooltip content="hi"><button>x</button></ry-tooltip>');
 * await expectEqual(el.state, 'closed', 'starts closed');
 * await expectEvent(el, 'ry:show', () => el.show());
 * await expectEqual(el.state, 'open');
 * await expectEvent(el, 'ry:hide', () => el.hide());
 * await expectEqual(el.state, 'closed');
 * ```
 *
 * @tutorial position-reflects-on-show
 * ```js
 * // data-ry-position is only set on the host when the tooltip opens.
 * // Before show(), the attribute should be absent — CSS uses it to drive
 * // the directional transform.
 * const el = mount('<ry-tooltip content="hi" position="bottom"><button>x</button></ry-tooltip>');
 * await expectEqual(el.hasAttribute('data-ry-position'), false, 'no position before show');
 * el.show();
 * await expectAttr(el, 'data-ry-position', 'bottom', 'position reflects after show');
 * ```
 *
 * @tutorial aria-describedby-links-trigger-to-content
 * ```js
 * // a11y contract: the trigger must reference the tooltip's id via
 * // aria-describedby so screen readers announce the tooltip when the
 * // trigger is focused.
 * const el = mount('<ry-tooltip content="hello"><button>x</button></ry-tooltip>');
 * const trigger = el.firstElementChild;
 * const content = el.querySelector('[data-ry-target="content"]');
 * await expectEqual(content.id.length > 0, true, 'tooltip content has an id');
 * await expectAttr(trigger, 'aria-describedby', content.id, 'trigger links to tooltip via aria-describedby');
 * await expectAttr(content, 'role', 'tooltip');
 * ```
 *
 * @tutorial escape-hides-when-open
 * ```js
 * const el = mount('<ry-tooltip content="hi"><button>x</button></ry-tooltip>');
 * el.show();
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'closed', 'escape closes when open');
 * ```
 *
 * @tutorial escape-is-noop-when-closed
 * ```js
 * // Wrong assumption: Escape always fires ry:hide. It does NOT fire when
 * // the tooltip is already closed — there is no transition to emit.
 * const el = mount('<ry-tooltip content="hi"><button>x</button></ry-tooltip>');
 * const fired = [];
 * el.addEventListener('ry:hide', () => fired.push(1));
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(fired.length, 0, 'no ry:hide when already closed');
 * ```
 *
 * @tutorial show-is-idempotent
 * ```js
 * const el = mount('<ry-tooltip content="hi"><button>x</button></ry-tooltip>');
 * const fired = [];
 * el.addEventListener('ry:show', () => fired.push(1));
 * el.show(); el.show(); el.show();
 * await expectEqual(fired.length, 1, 'show() is idempotent');
 * ```
 *
 * @tutorial beforeshow-veto
 * ```js
 * // preventDefault() on ry:beforeshow keeps the tooltip closed.
 * const el = mount('<ry-tooltip content="hi"><button>x</button></ry-tooltip>');
 * const shown = [];
 * el.addEventListener('ry:show', () => shown.push(1));
 * el.addEventListener('ry:beforeshow', (e) => e.preventDefault(), { once: true });
 * el.show();
 * await expectEqual(el.state, 'closed', 'vetoed show leaves it closed');
 * await expectEqual(el.hasAttribute('data-ry-position'), false, 'no position written');
 * await expectEqual(shown.length, 0, 'ry:show did not fire');
 * el.show();
 * await expectEqual(el.state, 'open', 'without the veto, show() works');
 * await expectEqual(shown.length, 1, 'ry:show fired once');
 * ```
 *
 * @tutorial beforehide-veto
 * ```js
 * // preventDefault() on ry:beforehide keeps the tooltip open — Escape,
 * // mouseleave, and el.hide() all go through the same guard.
 * const el = mount('<ry-tooltip content="hi"><button>x</button></ry-tooltip>');
 * el.show();
 * const hidden = [];
 * el.addEventListener('ry:hide', () => hidden.push(1));
 * el.addEventListener('ry:beforehide', (e) => e.preventDefault(), { once: true });
 * document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'open', 'vetoed hide leaves it open');
 * await expectEqual(hidden.length, 0, 'ry:hide did not fire');
 * el.hide();
 * await expectEqual(el.state, 'closed', 'without the veto, hide() works');
 * await expectEqual(hidden.length, 1, 'ry:hide fired once');
 * ```
 *
 * @tutorial hide-cancels-pending-show
 * ```js
 * // Wrong assumption: scheduling a delayed show and then hiding still shows
 * // briefly after the delay. It does NOT — hide() clears the pending
 * // timeout so a rapid hover-on-then-off never shows the tooltip.
 * // We exercise the internal #scheduleShow path via the mouseenter listener.
 * const el = mount('<ry-tooltip content="hi" delay="50"><button id="t">x</button></ry-tooltip>');
 * const fired = [];
 * el.addEventListener('ry:show', () => fired.push(1));
 * el.querySelector('#t').dispatchEvent(new Event('mouseenter', { bubbles: true }));
 * el.querySelector('#t').dispatchEvent(new Event('mouseleave', { bubbles: true }));
 * await sleep(120);  // longer than the delay
 * await expectEqual(fired.length, 0, 'pending show was cancelled by hide');
 * ```
 *
 * @see ry-dropdown
 * @see ry-button
 */

import { RyElement } from '../core/ry-element.js';
import { attachFloating, type Placement } from '../core/ry-floating.js';

let tooltipIdCounter = 0;

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const POSITION_TO_PLACEMENT: Record<TooltipPosition, Placement> = {
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
};

export class RyTooltip extends RyElement {
  #showTimeout: ReturnType<typeof setTimeout> | null = null;
  #tooltipEl: HTMLElement | null = null;
  #tooltipId: string | null = null;
  #trigger: Element | null = null;
  #floatingCleanup: (() => void) | null = null;

  setup(): void {
    this.#tooltipId = `ry-tooltip-${++tooltipIdCounter}`;

    // Create tooltip element
    this.#createTooltip();

    // Find trigger (first child element)
    const trigger = this.firstElementChild;
    if (trigger) {
      this.#trigger = trigger;
      // Set ARIA
      trigger.setAttribute('aria-describedby', this.#tooltipId);

      // Mouse events
      this.on(trigger, 'mouseenter', this.#scheduleShow);
      this.on(trigger, 'mouseleave', this.hide);

      // Focus events
      this.on(trigger, 'focusin', this.#scheduleShow);
      this.on(trigger, 'focusout', this.hide);
    }

    // Escape to hide
    this.on(document, 'keydown', this.#handleKeydown);

    // Initialize state
    if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }
  }

  #createTooltip(): void {
    // data-ry-target for JS queries, class for CSS styling
    this.#tooltipEl = document.createElement('div');
    this.#tooltipEl.id = this.#tooltipId!;
    this.#tooltipEl.setAttribute('data-ry-target', 'content');
    this.#tooltipEl.className = 'ry-tooltip__content';
    this.#tooltipEl.setAttribute('role', 'tooltip');
    this.#tooltipEl.textContent = this.getAttribute('content') ?? '';
    this.appendChild(this.#tooltipEl);
  }

  #scheduleShow = (): void => {
    if (this.#showTimeout) clearTimeout(this.#showTimeout);
    const delay = Number(this.getAttribute('delay') ?? 200);
    if (delay <= 0) {
      this.show();
      return;
    }
    this.#showTimeout = setTimeout(() => this.show(), delay);
  };

  show(): void {
    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
      this.#showTimeout = null;
    }
    if (this.state === 'open') return;
    if (!this.emit('beforeshow')) return;
    const position = (this.getAttribute('position') ?? 'top') as TooltipPosition;
    // Host's data-ry-position reflects the REQUESTED position (existing contract).
    // The floating element's own data-ry-position will reflect the COMPUTED
    // placement (after flip) — used for arrow direction and CSS hooks that
    // need the actual side after viewport-aware repositioning.
    this.setAttribute('data-ry-position', position);
    this.state = 'open';

    // Anchor via Floating UI
    if (this.#trigger && this.#tooltipEl) {
      this.#floatingCleanup?.();
      this.#floatingCleanup = attachFloating(this.#trigger, this.#tooltipEl, {
        placement: POSITION_TO_PLACEMENT[position],
        offset: 8,
        flip: true,
        shift: true,
        reflectPlacementAttribute: true,
      });
    }

    this.emit('show');
  }

  hide = (): void => {
    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
      this.#showTimeout = null;
    }
    if (this.state === 'closed') return;
    if (!this.emit('beforehide')) return;
    this.state = 'closed';

    // Detach Floating UI
    this.#floatingCleanup?.();
    this.#floatingCleanup = null;

    this.emit('hide');
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state === 'open') {
      this.hide();
    }
  };

  teardown(): void {
    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
    }
    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
  }
}

customElements.define('ry-tooltip', RyTooltip);
